// src/utilidades/brandingDocumento.js
/**
 * Aplica la identidad del gym al documento: título, favicon, apple-touch-icon,
 * theme-color y MANIFEST dinámico (blob). Fuente única usada por:
 *   - GymContext (app autenticada)
 *   - FormularioLogin (página /login/{slug}, ANTES de iniciar sesión)
 *   - Tienda pública y «Mis pedidos» (aplicarIdentidadTienda, sin tocar gymSlug)
 *
 * Por qué importa: Android congela el manifest EN EL MOMENTO de "Agregar a
 * pantalla de inicio" y el iPhone toma el apple-touch-icon de ese instante.
 *
 * Un solo logo por gimnasio: el que se sube con «Subir logo» (Ajustes o
 * superadmin). Orden del ícono (direcciones reales primero, que es lo que
 * iPhone y Android toman sin problemas):
 *   1. config.iconos { grande 512, apple 180 } en img.allgym.app, generados al subirlo
 *   2. el mismo logo en base64 (config.logoBase64)
 *   3. ícono genérico de AllGym
 *
 * localStorage('gymSlug') (no sessionStorage): el script pre-React de
 * index.html lo lee al abrir la app en pestañas/sesiones nuevas.
 *
 * Alcance del panel: '/' (scope). Sin él, el alcance por defecto es la carpeta
 * del start_url (/login/): /panel y /tienda/pedidos quedaban «fuera» de la app
 * instalada y Android atribuía los avisos push a Chrome (logo de Chrome y clic
 * que abre el navegador). id = start_url, el mismo id que ya tenían las
 * instalaciones existentes, para que Chrome las actualice en vez de duplicarlas.
 */

const ICONO_GENERICO = '/allgym-512.png';
const esUrlIcono = v => typeof v === 'string' && /^https:\/\/[^\s"'<>]+\.png$/.test(v);

const tipoDe = url => (url.startsWith('data:') ? url.slice(5, url.indexOf(';')) : url.endsWith('.svg') ? 'image/svg+xml' : 'image/png');

const aplicarIconos = ({ nombreCorto, nombreLargo, iconoUrl, appleUrl, startUrl, alcance, colorPrimario, fondoApp }) => {
  const favicon   = document.getElementById('favicon');
  const appleIcon = document.getElementById('apple-icon');
  if (favicon)   { favicon.href = iconoUrl; favicon.type = tipoDe(iconoUrl); }
  if (appleIcon) appleIcon.href = appleUrl || iconoUrl;

  const manifest = {
    short_name: nombreCorto,
    name:       nombreLargo,
    icons: [
      { src: iconoUrl, type: tipoDe(iconoUrl), sizes: '512x512', purpose: 'any' }
    ],
    start_url:        startUrl,
    ...(alcance ? { id: startUrl, scope: alcance } : {}),
    display:          'standalone',
    theme_color:      colorPrimario,
    background_color: fondoApp
  };
  const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const manifestLink = document.getElementById('manifest-link');
  if (manifestLink) {
    const oldHref = manifestLink.href;
    manifestLink.href = URL.createObjectURL(blob);
    if (oldHref.startsWith('blob:')) URL.revokeObjectURL(oldHref);
  }
};

// Íconos según la preferencia de arriba.
const aplicarConPreferencia = ({ logoBase64, iconos, ...resto }) => {
  const base64 = /^data:image\/(png|jpeg|webp);base64,/.test(logoBase64 || '') ? logoBase64 : '';
  const grande = esUrlIcono(iconos?.grande) ? iconos.grande : '';
  const apple = esUrlIcono(iconos?.apple) ? iconos.apple : '';
  const iconoUrl = grande || base64 || `${window.location.origin}${ICONO_GENERICO}`;
  aplicarIconos({ ...resto, iconoUrl, appleUrl: apple || iconoUrl });
};

export const aplicarBrandingDocumento = ({
  gymId,
  nombre,
  logoBase64,
  iconos,
  colorPrimario = '#DC2626',
  fondoApp = '#F9FAFB'
}) => {
  if (!gymId) return;
  const nombreFinal = nombre || gymId;

  // Título + metas
  document.title = nombreFinal;
  const metaTheme = document.getElementById('theme-color');
  if (metaTheme) metaTheme.content = colorPrimario;
  const metaDesc = document.getElementById('meta-description');
  if (metaDesc) metaDesc.content = `${nombreFinal} - Sistema de Gestión de Gimnasio`;

  aplicarConPreferencia({
    gymId, logoBase64, iconos, colorPrimario, fondoApp,
    nombreCorto: nombreFinal, nombreLargo: `${nombreFinal} Fitness`,
    startUrl: `${window.location.origin}/login/${gymId}`,
    alcance: `${window.location.origin}/`,
  });

  // Slug persistente para el script pre-React (index.html)
  try { localStorage.setItem('gymSlug', gymId); } catch (_) { /* modo privado */ }
};

/**
 * Tienda pública y «Mis pedidos»: ícono y manifest de ESA tienda. No guarda
 * gymSlug (no debe cambiar la marca del panel de un admin en este navegador).
 */
export const aplicarIdentidadTienda = ({ gymId, nombre, logoBase64, iconos, colorPrimario = '#272b8a', inicio }) => {
  if (!/^[a-z0-9]{1,100}$/.test(gymId || '')) return;
  const nombreFinal = nombre || 'Tienda';
  const metaTheme = document.getElementById('theme-color');
  if (metaTheme) metaTheme.content = colorPrimario;
  aplicarConPreferencia({
    gymId, logoBase64, iconos, colorPrimario, fondoApp: '#FFFFFF',
    nombreCorto: nombreFinal, nombreLargo: `Tienda · ${nombreFinal}`,
    startUrl: `${window.location.origin}${inicio || `/catalogo/${gymId}`}`,
  });
};

export default aplicarBrandingDocumento;
